"use client";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { api, Case, SubscriptionStatus } from "@/lib/api";
import { CaseCard } from "@/components/CaseCard";
import { AppShell } from "@/components/AppShell";
import { PageHelp } from "@/components/PageHelp";
import { getToken } from "@/lib/auth";

type MeData = { id: number; name: string; email: string; consent_accepted: boolean; consent_current: boolean };

function FreeBanner({ status }: { status: SubscriptionStatus }) {
  const t = useTranslations("dashboard.free");
  const used = status.sessions_used_this_month ?? 0;
  const limit = status.sessions_limit ?? 3;
  return (
    <div
      className="mb-6 flex items-center justify-between rounded-xl px-4 py-3 text-sm"
      style={{ background: 'var(--accent-light)', border: '1px solid rgba(15,76,76,0.12)', color: 'var(--accent)' }}
    >
      <span>
        <span className="font-semibold">{t("banner")}</span> — {t("used", { used, limit })}
      </span>
      <a
        href="/pricing"
        className="ml-4 shrink-0 rounded-lg px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
        style={{ background: 'var(--accent)' }}
      >
        {t("upgrade")}
      </a>
    </div>
  );
}

export default function Dashboard() {
  const tHeader = useTranslations("dashboard.header");
  const tCustom = useTranslations("dashboard.customPatients");
  const tView = useTranslations("dashboard.view");
  const tRecord = useTranslations("dashboard.practiceRecord");
  const tCommon = useTranslations("common");

  const [cases, setCases] = useState<Case[]>([]);
  const [sessionCounts, setSessionCounts] = useState<Record<number, number>>({});
  // Whether /practice-record would render an actual document rather than its empty
  // state. Answered by the same number the record itself gates on.
  const [recordReady, setRecordReady] = useState(false);
  // patient_case_id -> number of supervisor reports the student has never opened.
  const [unreadReports, setUnreadReports] = useState<Record<number, number>>({});
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [me, setMe] = useState<MeData | null>(null);
  const [view, setView] = useState<"card" | "list">("card");
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Pending action to resume after the user supplies a BYO key inline:
  // either start a session for a case, or open the create-patient form.
  const router = useRouter();

  // Kept out of the main Promise.all on purpose: the unread badge is decorative, so
  // a failure here must never turn into the page-wide "couldn't load" error state.
  const loadUnreadReports = useCallback(() => {
    api.getUnreadReports()
      .then((data) => {
        const map: Record<number, number> = {};
        data.cases.forEach((entry) => { map[entry.patient_case_id] = entry.unread; });
        setUnreadReports(map);
      })
      .catch(() => { /* no badge is better than a broken dashboard */ });
  }, []);

  useEffect(() => {
    if (!getToken()) { router.push("/login"); return; }
    setLoadError(false);
    loadUnreadReports();

    Promise.all([
      api.getCases(),
      api.getSubscriptionStatus(),
      api.getMe(),
    ]).then(([loaded, status, meData]) => {
      // Hukuki onay yoksa (yarım kalmış kayıt) VEYA kabul edilen sürüm eskiyse
      // uygulamaya girilemez; metin güncellenince herkes onay ekranından geçer.
      if (!meData.consent_current) { router.replace("/consent"); return; }
      // Hastaları isme göre alfabetik (TR) sırala — Aylin, Emre, Zeynep …
      setCases([...loaded].sort((a, b) => a.name.localeCompare(b.name, "tr")));
      setSubStatus(status);
      setMe(meData);
      Promise.all(loaded.map((c) => api.getCaseState(c.id).then((s) => ({ id: c.id, count: s.session_count }))))
        .then((results) => {
          const map: Record<number, number> = {};
          results.forEach(({ id, count }) => { map[id] = count; });
          setSessionCounts(map);
          // Only ask whether the practice record has content once some patient has
          // actually been supervised — that keeps the extra request off the path of
          // every new account. It IS a second question rather than a guess from
          // session_count, because the two answers can disagree: session_count rises
          // with every applied evaluation, while the record needs sessions carrying
          // observed skill scores. Promising a record that opens empty is worse than
          // showing the pointer a moment late.
          if (Object.values(map).some((n) => n > 0)) {
            api.getProgress()
              .then((p) => setRecordReady((p.stats?.evaluated_sessions ?? 0) > 0))
              .catch(() => { /* pointer stays hidden; the sidebar route still works */ });
          }
        })
        .catch(() => { /* per-case counts are non-critical; leave at 0 */ });
    }).catch(() => {
      // A genuine 401 already redirects to /login inside the request wrapper, so
      // reaching here means a non-auth failure (server/network). Don't bounce the
      // user to login (that reads as "you were logged out" and destroys trust) —
      // show a recoverable inline error with a retry instead.
      setLoadError(true);
    });
  }, [router, reloadKey, loadUnreadReports]);

  // Returning from a report normally remounts this page, which refetches on its own.
  // This covers the cases where it doesn't — a restored tab, or a report read in a
  // second tab — so the badge can never linger after the report has been read.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") loadUnreadReports();
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [loadUnreadReports]);

  useEffect(() => {
    const saved = localStorage.getItem("pp_cases_view");
    if (saved === "list" || saved === "card") setView(saved);
  }, []);

  function changeView(v: "card" | "list") {
    setView(v);
    localStorage.setItem("pp_cases_view", v);
  }

  async function proceedStart(c: Case) {
    const session = await api.startSession(c.id);
    router.push(`/session/${session.id}?patient=${encodeURIComponent(c.name)}&age=${c.age}`);
  }

  async function handleStart(c: Case) {
    try {
      await proceedStart(c);
    } catch (e) {
      let msg = e instanceof Error ? e.message : "";
      try {
        const parsed = JSON.parse(msg);
        // detail may be a string (free 402) or an object {code, message}.
        msg = parsed?.detail?.message ?? parsed?.detail ?? msg;
      } catch { /* plain text */ }
      // The allowance is used up (402) — surface the message and route to
      // pricing so the user can upgrade.
      alert(msg);
      router.push("/pricing");
    }
  }

  function handleCreatePatient() {
    router.push("/create-patient");
  }

  const isFree = subStatus?.tier === "free";

  return (
    <AppShell active="cases">
      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
              {tHeader("title")}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2">{tHeader("subtitle")}</p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {subStatus && (
              <div className="flex items-center gap-1 p-1 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]">
              <button
                onClick={() => changeView("card")}
                title={tView("card")}
                aria-label={tView("card")}
                className={`p-1.5 rounded-lg transition-colors ${view === "card" ? "bg-[var(--surface)] text-[var(--accent)] shadow-sm" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.7} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
                </svg>
              </button>
              <button
                onClick={() => changeView("list")}
                title={tView("list")}
                aria-label={tView("list")}
                className={`p-1.5 rounded-lg transition-colors ${view === "list" ? "bg-[var(--surface)] text-[var(--accent)] shadow-sm" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.7} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
              </button>
              </div>
            )}
            <PageHelp topic="dashboard" />
          </div>
        </div>

        {loadError && (
          <div
            className="mb-6 flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm"
            style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
          >
            <span>{tCommon("loadError")}</span>
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              className="shrink-0 rounded-lg px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
              style={{ background: '#b91c1c' }}
            >
              {tCommon("retry")}
            </button>
          </div>
        )}

        {isFree && subStatus && <FreeBanner status={subStatus} />}

        {/* Quiet pointer to the printable record. The dashboard is already dense
            with cards, so this stays a single low-contrast line rather than a card. */}
        {recordReady && (
          <div
            className="mb-6 flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-sm"
            style={{ borderColor: "var(--border)", background: "var(--surface-alt)" }}
          >
            <span className="text-[var(--text-secondary)]">{tRecord("ready")}</span>
            <a
              href="/practice-record"
              className="shrink-0 text-xs font-semibold text-[var(--accent)] hover:underline"
            >
              {tRecord("view")}
            </a>
          </div>
        )}

        <div className={view === "list" ? "flex flex-col gap-3" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
          {cases.map((c) => (
            <CaseCard
              key={c.id}
              c={c}
              sessionCount={sessionCounts[c.id] ?? 0}
              onStart={() => handleStart(c)}
              view={view}
              unreadReports={unreadReports[c.id] ?? 0}
            />
          ))}
        </div>

        {subStatus?.tier === "pro" && (
          <div className="mt-8 border-t border-[var(--border)] pt-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-[var(--text-primary)]">{tCustom("title")}</h2>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">{tCustom("description")}</p>
              </div>
              <button
                type="button"
                onClick={handleCreatePatient}
                className="inline-flex items-center gap-2 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-colors"
                style={{ background: 'var(--accent)' }}
                onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-mid)')}
                onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
              >
                {tCustom("newButton")}
              </button>
            </div>
            {cases.filter(c => c.is_custom).length === 0 && (
              <p className="text-sm text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-xl p-6 text-center">
                {tCustom("empty")}
              </p>
            )}
          </div>
        )}
      </main>
    </AppShell>
  );
}
